import { useAuth } from "../_core/hooks/useAuth";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Link } from "wouter";
import { 
  FolderOpen, 
  Image, 
  QrCode, 
  ShoppingCart, 
  Settings, 
  BarChart,
  TrendingUp,
  Users,
  Camera,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import DashboardLayout from "../components/DashboardLayout";
import { APP_TITLE, APP_LOGO } from "../const";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  const { data: projects } = trpc.projects.list.useQuery();
  const { data: analytics } = trpc.analytics.getOverview.useQuery();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Card className="w-96 shadow-2xl border-0">
          <CardHeader className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                <Camera className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-2xl">{APP_TITLE}</CardTitle>
            </div>
            <CardDescription className="text-base">{t.auth.welcomeBack}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t.dashboard.pleaseLogin}
            </p>
            <Button asChild className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <Link href="/login">
                <span className="flex items-center gap-2">
                  {t.auth.signIn}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 p-8 text-white shadow-2xl">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-6 w-6" />
              <span className="text-sm font-medium opacity-90">{t.dashboard.welcomeBack}</span>
            </div>
            <h1 className="text-4xl font-bold mb-2">
              {t.nav.dashboard}
            </h1>
            <p className="text-lg opacity-90 max-w-2xl">
              {t.dashboard.manage}
            </p>
            <div className="mt-6 flex gap-3">
              <Button asChild variant="secondary" size="lg" className="bg-white/20 hover:bg-white/30 text-white border-white/30">
                <Link href="/projects">
                  <span className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    {t.dashboard.myProjects}
                  </span>
                </Link>
              </Button>
              <Button asChild variant="secondary" size="lg" className="bg-white/20 hover:bg-white/30 text-white border-white/30">
                <Link href="/analytics">
                  <span className="flex items-center gap-2">
                    <BarChart className="h-5 w-5" />
                    {t.nav.analytics}
                  </span>
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-blue-50 to-blue-100/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-blue-900">
                {t.stats.totalProjects}
              </CardTitle>
              <div className="p-2 bg-blue-500 rounded-lg">
                <FolderOpen className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-900">{projects?.length || 0}</div>
              <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {t.nav.projects}
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-purple-50 to-purple-100/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-purple-900">
                {t.stats.totalImages}
              </CardTitle>
              <div className="p-2 bg-purple-500 rounded-lg">
                <Image className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-900">{analytics?.imageCount || 0}</div>
              <p className="text-xs text-purple-600 mt-2 flex items-center gap-1">
                <Camera className="h-3 w-3" />
                {t.nav.images}
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-pink-50 to-pink-100/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-pink-900">
                {t.nav.accessCodes}
              </CardTitle>
              <div className="p-2 bg-pink-500 rounded-lg">
                <QrCode className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-pink-900">{analytics?.codeCount || 0}</div>
              <p className="text-xs text-pink-600 mt-2 flex items-center gap-1">
                <Users className="h-3 w-3" />
                {t.nav.accessCodes}
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-green-50 to-green-100/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-green-900">
                {t.stats.totalOrders}
              </CardTitle>
              <div className="p-2 bg-green-500 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-900">{analytics?.orderCount || 0}</div>
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {t.nav.orders}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            {t.dashboard.quickActions}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group bg-gradient-to-br from-white to-blue-50">
              <Link href="/projects">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                      <FolderOpen className="h-7 w-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{t.nav.projects}</CardTitle>
                      <CardDescription className="mt-1">
                        {t.dashboard.myProjects}
                      </CardDescription>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                </CardHeader>
              </Link>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group bg-gradient-to-br from-white to-purple-50">
              <Link href="/settings">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl group-hover:scale-110 transition-transform">
                      <Settings className="h-7 w-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{t.nav.settings}</CardTitle>
                      <CardDescription className="mt-1">
                        {t.dashboard.manageSettingsDescription}
                      </CardDescription>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                </CardHeader>
              </Link>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group bg-gradient-to-br from-white to-pink-50">
              <Link href="/analytics">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl group-hover:scale-110 transition-transform">
                      <BarChart className="h-7 w-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{t.nav.analytics}</CardTitle>
                      <CardDescription className="mt-1">
                        {t.analytics.subtitle}
                      </CardDescription>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>
                </CardHeader>
              </Link>
            </Card>
          </div>
        </div>

        {/* Recent Projects */}
        {projects && projects.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FolderOpen className="h-6 w-6 text-primary" />
                {t.dashboard.recentProjects}
              </h2>
              <Button variant="outline" asChild>
                <Link href="/projects">
                  <span className="flex items-center gap-2">
                    {t.dashboard.allProjects}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.slice(0, 3).map((project) => (
                <Card key={project.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="group-hover:text-primary transition-colors">{project.title}</CardTitle>
                        <CardDescription className="mt-1">#{project.projectNumber}</CardDescription>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        project.status === 'active' ? 'bg-green-100 text-green-700' :
                        project.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {project.status}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {project.description || t.projects.noDescription}
                    </p>
                    <Button variant="outline" size="sm" asChild className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Link href={`/projects/${project.id}/images`}>
                        <span className="flex items-center gap-2">
                          {t.common.view}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!projects || projects.length === 0) && (
          <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-50 to-gray-100">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="p-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full mb-6">
                <FolderOpen className="h-16 w-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">{t.dashboard.noProjectsTitle}</h3>
              <p className="text-muted-foreground mb-6 text-center max-w-md">
                {t.dashboard.noProjectsDescription}
              </p>
              <Button size="lg" asChild className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                <Link href="/projects">
                  <span className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    {t.dashboard.createFirstProject}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
